import ApiService from "./api";
import { EndpointKeys } from "@/config/api";
import {
  CreateTerminationRequest,
  CreateTerminationResponse,
  GetTerminationResponse,
  SubmitTerminationRequest,
  SubmitTerminationResponse,
  TerminationRecord,
  UpdateTerminationRequest,
  UpdateTerminationResponse,
  TerminationResponse,
  FinalPayComponent,
} from "@/types/termination";

/**
 * Service for handling termination-related API operations
 */
class TerminationService {
  /**
   * Get termination record for an employee
   */
  public static async getTermination(
    employeeId: string,
  ): Promise<TerminationRecord | null> {
    try {
      const response = await ApiService.request<TerminationResponse>(
        EndpointKeys.GET_TERMINATION,
        {
          urlParams: { employee_id: employeeId },
        },
      );

      if (!response.termination) {
        return null;
      }

      // Include the final_pay_components in the returned termination record
      const termination = {
        ...response.termination,
        final_pay_components: response.final_pay_components || response.termination.final_pay_components,
      };

      return termination;
    } catch (error) {
      console.error("Error fetching termination:", error);
      throw error;
    }
  }

  /**
   * Create a new termination record
   */
  public static async createTermination(
    data: CreateTerminationRequest,
  ): Promise<TerminationRecord> {
    try {
      const response = await ApiService.request<TerminationResponse>(
        EndpointKeys.CREATE_TERMINATION,
        {
          method: "POST",
          body: data,
        },
      );

      if (!response.success || !response.termination) {
        throw new Error(response.message || "Failed to create termination");
      }

      // Include the final_pay_components in the returned termination record
      const termination = {
        ...response.termination,
        final_pay_components: response.final_pay_components || response.termination.final_pay_components,
      };

      return termination;
    } catch (error) {
      console.error("Error creating termination:", error);
      throw error;
    }
  }

  /**
   * Update an existing termination record
   */
  public static async updateTermination(
    employeeId: string,
    data: UpdateTerminationRequest,
  ): Promise<TerminationRecord> {
    try {
      const response = await ApiService.request<TerminationResponse>(
        EndpointKeys.UPDATE_TERMINATION,
        {
          method: "POST",
          urlParams: { termination_id: employeeId },
          body: data,
        },
      );

      if (!response.success || !response.termination) {
        throw new Error(response.message || "Failed to update termination");
      }

      // Include the final_pay_components in the returned termination record
      const termination = {
        ...response.termination,
        final_pay_components: response.final_pay_components || response.termination.final_pay_components,
      };

      return termination;
    } catch (error) {
      console.error("Error updating termination:", error);
      throw error;
    }
  }

  /**
   * Submit a termination for processing
   */
  public static async submitTermination(
    employeeId: string,
  ): Promise<TerminationRecord> {
    try {
      const response = await ApiService.request<TerminationResponse>(
        EndpointKeys.SUBMIT_TERMINATION,
        {
          method: "POST",
          urlParams: { employee_id: employeeId },
        },
      );

      if (!response.success || !response.termination) {
        throw new Error(response.message || "Failed to submit termination");
      }

      // Include the final_pay_components in the returned termination record
      const termination = {
        ...response.termination,
        final_pay_components: response.final_pay_components || response.termination.final_pay_components,
      };

      return termination;
    } catch (error) {
      console.error("Error submitting termination:", error);
      throw error;
    }
  }

  /**
   * Check if an employee has an active termination process
   */
  public static async hasActiveTermination(
    employeeId: string,
  ): Promise<boolean> {
    try {
      const termination = await this.getTermination(employeeId);
      return (
        !!termination &&
        (termination.workflow_status === "draft" ||
          termination.workflow_status === "pending")
      );
    } catch (error) {
      console.error("Error checking active termination:", error);
      return false;
    }
  }

  /**
   * Calculate notice period based on length of service and reason
   * This is a placeholder for future implementation
   * @param startDate The employee's start date
   * @param terminationDate The termination date
   * @param reason The termination reason
   * @param contractualNoticeWeeks Optional contractual notice period in weeks
   * @returns The calculated notice period
   */
  static calculateNoticePeriod(
    startDate: string,
    terminationDate: string,
    reason: string,
    contractualNoticeWeeks?: number,
  ): { weeks: number; payThroughDate: string } {
    // This is a placeholder - actual calculation would be implemented here
    // based on UK employment law and company policies
    const weeks = 4; // Placeholder value

    // Calculate pay-through date (termination date + notice period)
    const termDate = new Date(terminationDate);
    const payThroughDate = new Date(termDate);
    payThroughDate.setDate(termDate.getDate() + weeks * 7);

    return {
      weeks,
      payThroughDate: payThroughDate.toISOString().split("T")[0],
    };
  }

  /**
   * Calculate components of final pay
   * This is a placeholder for future implementation
   * @param employeeId The ID of the employee
   * @param terminationDate The termination date
   * @param payThroughDate The pay-through date after notice period
   * @returns Components of the final pay
   */
  static calculateFinalPay(
    employeeId: string,
    terminationDate: string,
    payThroughDate: string,
  ): Array<{ name: string; description: string; amount: number }> {
    // This is a placeholder - actual calculation would be implemented here
    // based on salary, holiday accrual, etc.
    return [
      {
        name: "Base salary",
        description: "Prorated to pay-through date",
        amount: 0,
      },
      { name: "Accrued holiday", description: "Unused holiday pay", amount: 0 },
      {
        name: "Notice pay",
        description: "Payment in lieu of notice",
        amount: 0,
      },
    ];
  }

  /**
   * Get available final pay components
   */
  public static async getFinalPayComponents(): Promise<FinalPayComponent[]> {
    try {
      const response = await ApiService.request<{ success: boolean; components: FinalPayComponent[] }>(
        EndpointKeys.GET_FINAL_PAY_COMPONENTS,
      );

      if (!response.success || !response.components) {
        return [];
      }

      return response.components;
    } catch (error) {
      console.error("Error fetching final pay components:", error);
      return [];
    }
  }
}

export default TerminationService;
